import { Link, useParams } from "react-router";
import { ChevronLeft, Target } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useAuth } from "@/lib/auth-context";
import { useAdminPartnersList } from "@/features/admin/partners/use-admin-partners";
import { useGoal } from "./use-goal";
import { useGoalTargets } from "./use-goal-targets";
import { GoalEditor } from "./goal-editor";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/goals/[id]/page.tsx.

export function GoalDetailPage() {
  const { id } = useParams<{ id: string }>();
  const session = useAuth().session;
  const canManage = !!session?.permissions.includes("goals.manage");

  const goalQuery = useGoal(id);
  const targetsQuery = useGoalTargets(id);
  const partnersQuery = useAdminPartnersList();

  if (goalQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  const goal = goalQuery.data;
  if (!goal) {
    return (
      <EmptyState
        icon={Target}
        title="Goal not found"
        description="This goal may have been deleted. Return to the list to see what's available."
      />
    );
  }

  const partners = (partnersQuery.data ?? []).map((p) => ({
    id: p.id,
    company_name: p.company_name,
  }));

  return (
    <div>
      <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Link
          to="/admin/goals"
          className="inline-flex items-center gap-1 hover:text-foreground"
        >
          <ChevronLeft className="h-4 w-4" /> Goals
        </Link>
      </div>

      <GoalEditor
        goal={goal}
        targets={targetsQuery.data ?? []}
        partners={partners}
        canManage={canManage}
      />
    </div>
  );
}
